import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Menu } from 'lucide-react';
import { routeList } from '../routes';
import { useAuth } from '../context/AuthContext';

const APP_NAME = 'AK Admin';

export default function AppShell() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login', { replace: true });
    };

    const currentRoute = useMemo(
        () =>
            routeList.find((route) =>
                matchPath({ path: route.path, end: true }, location.pathname)
            ) ?? routeList[0],
        [location.pathname]
    );

    useEffect(() => {
        document.title = `${currentRoute.title} | ${APP_NAME}`;
        setIsSidebarOpen(false);
    }, [currentRoute]);

    return (
        <div className="h-screen overflow-hidden bg-slate-50 text-slate-900">
            <div className="flex h-full">
                <aside
                    className={[
                        'fixed inset-y-0 left-0 z-40 w-64 border-r border-cyan-100 bg-white transition-transform lg:static lg:translate-x-0',
                        isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
                    ].join(' ')}
                >
                    <div className="flex h-full flex-col">
                        <div className="border-b border-cyan-100 px-4 py-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700">
                                {APP_NAME}
                            </p>
                            <h1 className="mt-1 text-lg font-semibold text-slate-900">Control Panel</h1>
                        </div>

                        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
                            {routeList.map((route) => (
                                <NavLink
                                    key={route.key}
                                    to={route.path}
                                    end={route.path === '/'}
                                    className={({ isActive }) =>
                                        [
                                            'block rounded-lg px-3 py-2 text-sm transition-colors',
                                            isActive
                                                ? 'bg-cyan-50 text-cyan-800'
                                                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                                        ].join(' ')
                                    }
                                >
                                    {route.label}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="border-t border-cyan-100 px-4 py-3 text-xs text-slate-500">
                            Ready for more pages
                        </div>
                    </div>
                </aside>

                {isSidebarOpen && (
                    <button
                        type="button"
                        aria-label="Close sidebar"
                        className="fixed inset-0 z-30 bg-slate-950/20 lg:hidden"
                        onClick={() => setIsSidebarOpen(false)}
                    />
                )}

                <div className="flex min-w-0 flex-1 flex-col">
                    <header className="flex h-16 items-center justify-between border-b border-cyan-100 bg-white px-4 sm:px-6">
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                aria-label="Open sidebar"
                                className="rounded-lg border border-cyan-100 p-2 text-slate-700 lg:hidden"
                                onClick={() => setIsSidebarOpen(true)}
                            >
                                <Menu size={18} />
                            </button>

                            <div>
                                <p className="text-xs uppercase tracking-[0.18em] text-cyan-700">Page</p>
                                <h2 className="text-base font-semibold text-slate-900">{currentRoute.title}</h2>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {user?.email && (
                                <span className="text-xs text-slate-500">{user.email}</span>
                            )}
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="flex items-center gap-1 rounded-lg border border-cyan-100 px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                            >
                                <LogOut size={14} />
                                Đăng xuất
                            </button>
                        </div>
                    </header>

                    <main className="scroll-surface flex-1 overflow-y-auto px-2 py-2 sm:px-3 sm:py-3">
                        <Outlet />
                    </main>

                    <footer className="border-t border-cyan-100 bg-white px-4 py-3 text-xs text-slate-500 sm:px-6">
                        {APP_NAME} admin interface
                    </footer>
                </div>
            </div>
        </div>
    );
}